import { Trans, useTranslation } from "react-i18next";
import { useMemo, useState, type ReactNode } from "react";
import { Loader2, Plus, Trash2, X } from "lucide-react";
import { Input as AriaInput, Label, TextField } from "react-aria-components";
import type { FacilityNode, KioskProfile } from "@arc/contracts";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import type { AuthSession } from "../../auth/keycloak.js";
import { useShowError } from "../../error/ErrorProvider.js";
import { useFacilities } from "../facilities/useFacilities.js";
import { facilityPathLabel } from "../facilities/facility-tree.js";
import { KioskForbiddenError, useCreateKioskProfile, useDeleteKioskProfile, useKioskProfiles, useUpdateKioskProfile } from "./useKiosk.js";

/**
 * Kiosk profiles (ADR-036) — the legacy "Generischer User" setup screen, rebuilt on a server-side record.
 *
 * The legacy wrote two hard-coded departments into a 365-day cookie and shipped four customer presets inline; here a
 * profile is data: any number of named departments, each with facilities, saved server-side so a change reaches every
 * device without re-visiting it. SuperAdmin-only, as the legacy entry was.
 */
interface DraftDepartment {
  key: string;
  name: string;
  facilityIds: number[];
}
interface Draft {
  name: string;
  departments: DraftDepartment[];
  /**
   * The concurrency token of the record this draft was OPENED from — `null` for a not-yet-created profile. It lives
   * on the draft, not read off the query cache at save time (diff panel, Medium): a 409 refetches the list, and the
   * token the next save sends must be the one the admin actually re-opened, never whichever copy the cache holds.
   */
  expectedUpdatedAt: string | null;
}
const newKey = () => Math.random().toString(36).slice(2);
const toDraft = (p: KioskProfile): Draft => ({
  name: p.name,
  departments: p.departments.map((d) => ({ key: `d${String(d.id)}`, name: d.name, facilityIds: [...d.facilityIds] })),
  expectedUpdatedAt: p.updatedAt,
});
const emptyDraft = (): Draft => ({ name: "", departments: [{ key: newKey(), name: "", facilityIds: [] }], expectedUpdatedAt: null });

export function KioskProfilesView({ session, heading }: { session: AuthSession; heading?: ReactNode }) {
  const { t } = useTranslation();
  const profiles = useKioskProfiles(session);
  const facilities = useFacilities(session);
  const create = useCreateKioskProfile(session);
  const update = useUpdateKioskProfile(session);
  const remove = useDeleteKioskProfile(session);
  const showError = useShowError();

  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [confirming, setConfirming] = useState<KioskProfile | null>(null);
  const [copied, setCopied] = useState(false);

  const list = useMemo(() => profiles.data ?? [], [profiles.data]);
  const selected = useMemo(() => list.find((p) => p.id === selectedId) ?? null, [list, selectedId]);
  // The draft is seeded by the USER ACTIONS that change what is open (open a row, New profile, a save that lands) —
  // never by an effect watching `selected` (diff panel, High). That effect fired on every identity change of the
  // memo: `New profile` nulled the selection a tick after seeding the empty form and wiped it, and a background
  // refetch of the open profile silently discarded the admin's unsaved edits.
  const openProfile = (p: KioskProfile) => {
    setSelectedId(p.id);
    setDraft(toDraft(p)); // re-seeds even when the row is already open — this is the "reopen" a stale save asks for
    setCopied(false);
  };

  const byId = useMemo(() => new Map((facilities.data ?? []).map((f) => [f.id, { name: f.name, parentId: f.parentId }] as const)), [facilities.data]);
  const pathOf = (id: number) => facilityPathLabel(byId, id);

  const withHeading = (body: ReactNode) => (
    <div className="flex flex-col gap-4">
      {heading}
      {body}
    </div>
  );
  if (profiles.isPending) {
    return withHeading(
      <p role="status" className="text-muted-foreground flex items-center gap-2 text-sm">
        <Loader2 aria-hidden="true" className="size-4 animate-spin" /> {t("viewer.loadingKioskProfiles")}
      </p>,
    );
  }
  if (profiles.error instanceof KioskForbiddenError) {
    return withHeading(<p className="text-muted-foreground max-w-prose text-sm">{t("viewer.kioskSuperAdminOnly")}</p>);
  }
  if (profiles.isError) {
    return withHeading(
      <div className="flex flex-col items-start gap-2">
        <p role="alert" className="text-destructive text-sm">
          {t("viewer.couldntLoadTheKioskProfiles")}
        </p>
        <Button variant="outline" size="sm" onPress={() => void profiles.refetch()}>
          {t("viewer.tryAgain")}
        </Button>
      </div>,
    );
  }

  const startNew = () => {
    setSelectedId(null);
    setDraft(emptyDraft());
    setCopied(false);
  };
  const patchDraft = (p: Partial<Draft>) => setDraft((d) => (d ? { ...d, ...p } : d));
  const patchDept = (key: string, p: Partial<DraftDepartment>) =>
    setDraft((d) => (d ? { ...d, departments: d.departments.map((x) => (x.key === key ? { ...x, ...p } : x)) } : d));

  const valid = draft !== null && draft.name.trim() !== "" && draft.departments.length > 0 && draft.departments.every((d) => d.name.trim() !== "");
  const save = () => {
    if (!draft || !valid) return;
    const body = { name: draft.name.trim(), departments: draft.departments.map((d) => ({ name: d.name.trim(), facilityIds: d.facilityIds })) };
    const onError = (e: Error) => showError(e);
    // `selected` (the record still in the list) decides WHICH operation; the draft supplies the token. If the profile
    // vanished under us, this falls through to create rather than PATCHing an id that is gone.
    if (selected && draft.expectedUpdatedAt !== null) {
      update.mutate(
        { id: selected.id, body: { ...body, expectedUpdatedAt: draft.expectedUpdatedAt } },
        // The saved record answers with the NEXT token, so a second save in a row works without waiting for the
        // list refetch to land.
        { onSuccess: (p) => setDraft((d) => (d ? { ...d, expectedUpdatedAt: p.updatedAt } : d)), onError },
      );
    } else {
      create.mutate(body, { onSuccess: (p) => openProfile(p), onError });
    }
  };
  const saving = create.isPending || update.isPending;

  const kioskLink = selected ? `${window.location.origin}/?arc_kiosk=${String(selected.id)}` : "";
  const copyLink = () => {
    void navigator.clipboard?.writeText(kioskLink).then(
      () => setCopied(true),
      () => setCopied(false),
    );
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 md:flex-row">
      <div className="border-border flex shrink-0 flex-col gap-3 md:w-72 md:overflow-y-auto md:border-r md:pr-3">
        {heading}
        <Button variant="outline" size="sm" onPress={startNew} className="self-start">
          <Plus aria-hidden="true" className="size-4" /> {t("viewer.newProfile")}
        </Button>
        {list.length === 0 ? (
          <p className="text-muted-foreground max-w-prose text-sm">
            {t("viewer.noKioskProfilesYetAProfile")}
          </p>
        ) : (
          <ul aria-label={t("viewer.kioskProfiles")} className="m-0 flex list-none flex-col gap-0.5 p-0">
            {list.map((p) => (
              <li key={p.id}>
                <button
                  type="button"
                  aria-current={p.id === selectedId ? "true" : undefined}
                  onClick={() => openProfile(p)}
                  className={cn(
                    "hover:bg-muted focus-visible:ring-ring/50 flex w-full cursor-pointer flex-col items-start rounded-md border-0 bg-transparent px-2 py-1.5 text-left text-sm outline-none focus-visible:ring-2",
                    p.id === selectedId && "bg-muted",
                  )}
                >
                  <span className="truncate font-medium">{p.name}</span>
                  <span className="text-muted-foreground text-xs">
                    {t("viewer.countDepartments", { count: p.departments.length })}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <section aria-label={t("viewer.kioskProfileEditor")} className="min-w-0 flex-1 md:overflow-y-auto">
        {draft === null ? (
          <p className="text-muted-foreground text-sm">{t("viewer.selectOrCreateProfile")}</p>
        ) : (
          <div className="flex max-w-3xl flex-col gap-5">
            <TextField value={draft.name} onChange={(name) => patchDraft({ name })} className="flex max-w-sm flex-col gap-1">
              <Label className="text-muted-foreground text-xs">{t("viewer.profileName")}</Label>
              <Input placeholder={t("viewer.eGLine3Terminal")} className="h-8" />
            </TextField>

            <div className="flex flex-col gap-4">
              {draft.departments.map((dept, i) => (
                <div key={dept.key} className="border-border flex flex-col gap-2 rounded-lg border p-3">
                  <div className="flex items-end gap-2">
                    <TextField value={dept.name} onChange={(name) => patchDept(dept.key, { name })} className="flex max-w-xs flex-1 flex-col gap-1">
                      <Label className="text-muted-foreground text-xs">{t("viewer.departmentN", { n: i + 1 })}</Label>
                      <Input placeholder={t("viewer.eGManufacturing")} className="h-8" />
                    </TextField>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      aria-label={t("viewer.removeDepartmentNamestring", { nameString: dept.name || String(i + 1) })}
                      isDisabled={draft.departments.length === 1}
                      onPress={() => patchDraft({ departments: draft.departments.filter((x) => x.key !== dept.key) })}
                    >
                      <Trash2 aria-hidden="true" className="size-4" />
                    </Button>
                  </div>
                  <DepartmentFacilities
                    label={dept.name || t("viewer.departmentN", { n: i + 1 })}
                    facilityIds={dept.facilityIds}
                    all={facilities.data ?? []}
                    pathOf={pathOf}
                    onChange={(facilityIds) => patchDept(dept.key, { facilityIds })}
                  />
                </div>
              ))}
              <Button
                variant="outline"
                size="sm"
                className="self-start"
                onPress={() => patchDraft({ departments: [...draft.departments, { key: newKey(), name: "", facilityIds: [] }] })}
              >
                <Plus aria-hidden="true" className="size-4" /> {t("viewer.addDepartment")}
              </Button>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Button variant="default" size="sm" isDisabled={!valid || saving} onPress={save}>
                {saving ? <Loader2 aria-hidden="true" className="size-4 animate-spin" /> : null}
                {selected ? t("viewer.saveChanges") : t("viewer.createProfile")}
              </Button>
              {selected ? (
                <>
                  <Button variant="outline" size="sm" onPress={copyLink}>
                    {copied ? t("viewer.linkCopied") : t("viewer.copyKioskLink")}
                  </Button>
                  <Button variant="destructive" size="sm" onPress={() => setConfirming(selected)}>
                    <Trash2 aria-hidden="true" className="size-4" /> {t("other.deleteButton")}
                  </Button>
                </>
              ) : null}
            </div>
            {selected ? (
              <p className="text-muted-foreground max-w-prose text-xs">
                {t("viewer.kioskLinkHint")}
              </p>
            ) : null}
          </div>
        )}
      </section>

      <ConfirmDialog
        isOpen={confirming !== null}
        title={t("viewer.deleteKioskProfile")}
        message={
          <>
            <Trans i18nKey="viewer.deleteProfileQ" components={{ name: <strong>{confirming?.name}</strong> }} />
            <br />
            {t("viewer.anyDeviceStillUsingItFalls")}
          </>
        }
        confirmLabel={t("other.deleteButton")}
        destructive
        isPending={remove.isPending}
        onConfirm={() => {
          const p = confirming;
          if (!p) return;
          remove.mutate(
            { id: p.id },
            {
              onSuccess: () => {
                setConfirming(null);
                if (selectedId === p.id) {
                  setSelectedId(null);
                  setDraft(null);
                }
              },
              onError: (e) => {
                setConfirming(null);
                showError(e);
              },
            },
          );
        }}
        onCancel={() => setConfirming(null)}
      />
    </div>
  );
}

/**
 * A department's facilities: removable chips + a type-to-add list — the same interaction the QR label picker uses,
 * which is the closest thing the legacy had (its "Bereich hinzufügen" opened a whole tree dialog per department).
 */
function DepartmentFacilities({
  label,
  facilityIds,
  all,
  pathOf,
  onChange,
}: {
  label: string;
  facilityIds: number[];
  all: readonly FacilityNode[];
  pathOf: (id: number) => string;
  onChange: (ids: number[]) => void;
}) {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const q = query.trim().toLowerCase();
  const candidates = useMemo(
    () => (q === "" ? [] : all.filter((f) => !f.archived && f.permission !== null && !facilityIds.includes(f.id) && f.name.toLowerCase().includes(q)).slice(0, 8)),
    [all, facilityIds, q],
  );
  const chosen = facilityIds.map((id) => ({ id, node: all.find((f) => f.id === id) ?? null }));

  return (
    <div className="flex flex-col gap-2">
      {chosen.length > 0 ? (
        <ul aria-label={t("viewer.facilitiesInLabel", { label: label })} className="m-0 flex list-none flex-wrap gap-2 p-0">
          {chosen.map(({ id, node }) => (
            <li key={id} className="border-border bg-muted/40 flex items-center gap-1.5 rounded-full border py-1 pr-1 pl-3 text-sm">
              <span className="flex flex-col leading-tight">
                <span>{node?.name ?? t("viewer.facilityN", { id })}</span>
                {pathOf(id) !== "" ? <span className="text-muted-foreground text-xs">{pathOf(id)}</span> : null}
              </span>
              <Button variant="ghost" size="icon-sm" aria-label={t("viewer.removeNamestringFromLabel", { nameString: node?.name ?? String(id), label: label })} className="size-6" onPress={() => onChange(facilityIds.filter((x) => x !== id))}>
                <X aria-hidden="true" className="size-3" />
              </Button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-muted-foreground text-sm italic">{t("viewer.noFacilityYet")}</p>
      )}
      <TextField value={query} onChange={setQuery} aria-label={t("viewer.addFacilityToLabel", { label: label })} className="max-w-sm">
        <AriaInput
          placeholder={t("viewer.typeAFacilityNameToAdd")}
          className="border-border bg-background focus-visible:border-primary focus-visible:ring-primary h-8 w-full rounded-md border px-2 text-sm outline-none focus-visible:ring-1"
        />
      </TextField>
      {candidates.length > 0 ? (
        <ul aria-label={t("viewer.matchingFacilitiesForLabel", { label: label })} className="border-border m-0 flex max-w-sm list-none flex-col divide-y rounded-lg border p-0">
          {candidates.map((f) => (
            <li key={f.id}>
              <button
                type="button"
                onClick={() => {
                  onChange([...facilityIds, f.id]);
                  setQuery("");
                }}
                className="hover:bg-muted flex w-full cursor-pointer items-center gap-2 border-0 bg-transparent px-3 py-1.5 text-left text-sm"
              >
                <Plus aria-hidden="true" className="text-muted-foreground size-3.5" />
                <span className="flex flex-col leading-tight">
                  <span>{f.name}</span>
                  {pathOf(f.id) !== "" ? <span className="text-muted-foreground text-xs">{pathOf(f.id)}</span> : null}
                </span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
